import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ItiString } from "@/iti-core-react/utils/iti-string";
import { ProfileDto } from "@/shared/dtos/profile-dto";
import { UserProfileDto } from "@/shared/dtos/user-profile-dto";
import { RootState } from "@/store";
import {
	setNavigationBlocked,
	showNavigationBlockedMessage,
} from "@/store/navigationBlock/navigationBlockActions";
import Button from "devextreme-react/cjs/button";
import ContextMenu from "devextreme-react/cjs/context-menu";
import TreeView from "devextreme-react/cjs/tree-view";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../iti-core-react/contexts/auth";
import "./UserPanel.scss";

interface UserPanelProps {
	menuMode: "context" | "list";
	menuOptions: any[];
	userProfile: UserProfileDto;
}

export default function UserPanel({
	menuMode,
	menuOptions,
}: Readonly<UserPanelProps>) {
	const { user, signOut, profile } = useContext(AuthContext);
	const navigate = useNavigate();
	const { t } = useLanguage();
	const dispatch = useDispatch();

	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [showMobileList, setShowMobileList] = useState(false);
	const isNavigationBlocked = useSelector(
		(state: RootState) => state.navigationBlock.isNavigationBlocked,
	);
	const isNavigationBlockedRef = useRef(isNavigationBlocked);
	const [currentProfile, setCurrentProfile] = useState<ProfileDto>(
		profile ?? new ProfileDto({ fullName: "", email: "" }),
	);

	useEffect(() => {
		isNavigationBlockedRef.current = isNavigationBlocked;
	}, [isNavigationBlocked]);

	useEffect(() => {
		if (profile && JSON.stringify(profile) !== JSON.stringify(currentProfile)) {
			setCurrentProfile(profile);
		}
	}, [profile]);

	const getLocaleText = useCallback(
		(item: any) => {
			if (!ItiString.isNullOrWhiteSpace(item.i18n)) {
				return t(item.i18n);
			}
			return item.text;
		},
		[t],
	);

	const getInitials = (fullName: string): string => {
		const names = fullName.split(" ");
		return `${names[0]?.[0] || ""}${names[1]?.[0] || ""}`.toUpperCase();
	};

	const handleNavigation = useCallback(
		async (e: any) => {
			if (menuMode === "list") {
				setShowMobileList(false);
			} else {
				setIsMenuOpen(false);
			}

			// Solo verificar bloqueo de navegación si estamos en la ruta second-step
			const currentPath = window.location.pathname;
			const isInSecondStep = currentPath.includes("/second-step/");

			if (isNavigationBlockedRef.current && isInSecondStep) {
				const canNavigate = await dispatch(showNavigationBlockedMessage());
				if (canNavigate) {
					if (e?.itemData?.link) {
						navigate(e.itemData.link);
					}
					dispatch(setNavigationBlocked(false));
				}
			} else if (e?.itemData?.link) {
				navigate(e.itemData.link);
			}
		},
		[dispatch, navigate, menuMode],
	);

	const handleItemClick = useCallback(
		async (e: any) => {
			if (e.itemData.link === "/log") {
				if (menuMode === "list") {
					setShowMobileList(false);
				} else {
					setIsMenuOpen(false);
				}
				signOut?.();
			} else if (e.itemData.link === "/my-organization") {
				// Solo verificar bloqueo de navegación si estamos en la ruta second-step
				const currentPath = window.location.pathname;
				const isInSecondStep = currentPath.includes("/second-step/");

				if (isNavigationBlockedRef.current && isInSecondStep) {
					const canNavigate = await dispatch(showNavigationBlockedMessage());
					if (canNavigate) {
						const profile = JSON.parse(localStorage.getItem("profile") || "{}");
						if (profile) {
							navigate(`/organization/${profile.organizationId}`);
						}
						dispatch(setNavigationBlocked(false));
					}
				} else {
					const profile = JSON.parse(localStorage.getItem("profile") || "{}");
					if (profile) {
						navigate(`/organization/${profile.organizationId}`);
					}
				}
				if (menuMode === "list") {
					setShowMobileList(false);
				} else {
					setIsMenuOpen(false);
				}
			} else {
				await handleNavigation(e);
			}
		},
		[handleNavigation, signOut, menuMode, navigate, dispatch],
	);

	const processedMenuItems = useMemo(() => {
		const processItem = (item: any) => ({
			...item,
			text: getLocaleText(item),
			items: item.items ? item.items.map(processItem) : item.items,
		});

		const processedMenuOptions = menuOptions.map(processItem);

		const index = processedMenuOptions.findIndex(
			(item) => item.i18n === "user-menu.logout" && item.icon === "mdi mdi-run",
		);

		if (index !== -1) {
			const [logoutItem] = processedMenuOptions.splice(index, 1);
			processedMenuOptions.push(logoutItem);
		}

		return processedMenuOptions;
	}, [menuOptions, getLocaleText]);

	const renderUserInfo = useMemo(
		() => (
			<div className="row user-container">
				<div className="col-auto user-initials">
					{getInitials(profile?.fullName || "")}
				</div>
				<div className="col">
					<div className="row h-50 p-0 m-0">
						<div className="col user-full-name">{profile?.fullName}</div>
					</div>
					<div className="row h-50 m-0 p-0">
						<div className="col user-email">{user?.userName}</div>
					</div>
				</div>
			</div>
		),
		[profile],
	);

	const handleInteraction = () => {
		if (menuMode === "list") {
			setShowMobileList(!showMobileList);
		} else {
			setIsMenuOpen(!isMenuOpen);
		}
	};

	return (
		<div className="user-panel">
			<Button
				className="user-panel-button"
				stylingMode="text"
				width="100%"
				onClick={handleInteraction}
			>
				{renderUserInfo}
			</Button>

			{menuMode === "context" && (
				<ContextMenu
					items={processedMenuItems || []}
					target=".user-button"
					displayExpr="text"
					visible={isMenuOpen}
					onHiding={() => setIsMenuOpen(false)}
					onShowing={() => setIsMenuOpen(true)}
					cssClass="user-menu"
					width={250}
					onItemClick={handleItemClick}
					position={{
						my: { x: "right", y: "top" },
						at: { x: "right", y: "bottom" },
						collision: "flipfit",
					}}
					animation={{
						show: {
							type: "fade",
							duration: 200,
							from: 0,
							to: 1,
						},
						hide: {
							type: "fade",
							duration: 200,
							from: 1,
							to: 0,
						},
					}}
				/>
			)}

			{menuMode === "list" && showMobileList && (
				<div className="mobile-user-list">
					<TreeView
						items={processedMenuItems}
						displayExpr="text"
						keyExpr="id"
						onItemClick={handleItemClick}
						width={300}
					/>
				</div>
			)}
		</div>
	);
}
